import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  DatePicker,
  MonthPicker,
  RegularModal,
  Select,
  YearSelect,
} from '@/components_deprecated'
import type { ExportActivitiesForm } from '@/modules/activities/types'
import { Field, Form } from '@/modules/companies'
import React, { useEffect } from 'react'
import {
  ExportDate,
  ExportFormat,
  ExportMode,
  ExportOption,
  ExportType,
} from '@/modules/exports'
import {
  useExportFormatOptions,
  useExportOptions,
} from '@/modules/exports/hooks'
import { MonthWeekToggle } from '@/components_deprecated/Toggle/MonthWeekToggle'
import { DateRangePickerCustom } from '../../../../components_deprecated/DatePicker/DateRangePicker/DateRangePickerCustom'
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns'
import styled from 'styled-components'
import { WeekPicker } from '../../../../components_deprecated/MonthPicker/WeekPicker'
import { Checkbox } from '@/components'
import VehicleSelect from '@/components/vehicle-select'
import { FieldError } from '@/components/ui/field'

interface ExportActivitiesModalProps {
  onSubmit: PromptResolve<ExportActivitiesForm | null>
  dateStart: Date
  dateEnd: Date
}

const ModalStyled = styled(RegularModal)`
  @media screen and (max-width: 768px) {
    max-height: 92vh;
  }
`

export const ExportActivitiesModal: React.FC<ExportActivitiesModalProps> = ({
  onSubmit,
  dateEnd,
  dateStart,
}) => {
  const translations = useTranslate({
    exportDrivingData: 'activities.export-driving-data',
    export: 'common.export',

    hours: 'common.hours',
    diet: 'common.diet',
    email: 'common.email',
    download: 'common.download',
    vehicle: 'common.vehicle',
    type: 'common.type',
    exportOption: 'common.export-type',
    exportFormat: 'common.export-format',
    driver: 'activities.driver',
    exportWeekly: 'activities.export-monthly-weekly',
    exportWeeklyVehicle: 'activities.export-monthly-weekly-vehicle',
    pauseExport: 'activities.pause-export',
    both: 'common.both',
    modeType: 'common.mode',
    detailed: 'common.detailed',

    date: 'common.date',
    perDay: 'common.day',
    perMonth: 'common.month',
    perYear: 'common.year',
    period: 'common.period',
    required: 'common.required',
    week: 'common.week',
  })

  const {
    formValues: form,
    updateForm,
    submitHandler: submit,
    formValid,
    formErrors: errors,
  } = useForm<ExportActivitiesForm>({
    values: {
      modeType: ExportMode.BOTH,
      vehicle: null,
      date: startOfDay(dateStart),
      dateEnd: endOfDay(dateEnd),
      exportType: ExportType.ACTIVITIES,
      exportOption: ExportOption.DOWNLOAD,
      exportFormat: ExportFormat.PDF,
      weekly: false,
      dateType: ExportDate.CUSTOM,
      detailed: false,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-activites-form',
        transformValues: {
          exportType: value => value as ExportType,
          exportFormat: value => value as ExportFormat,
          exportOption: value => value as ExportOption,
          weekly: value => value as boolean,
        },
      },
    },
    validators: {
      vehicle: (
        value: string | null,
        values: ExportActivitiesForm
      ): string | null =>
        values.exportType === ExportType.VEHICLE &&
        [ExportMode.BOTH, ExportMode.VEHICLE].includes(values.modeType) &&
        !value
          ? translations.required
          : null,
    },
  })

  const exportMods = [
    {
      value: ExportMode.VEHICLE,
      label: translations.vehicle,
    },
    {
      value: ExportMode.DRIVER,
      label: translations.driver,
    },
    {
      value: ExportMode.BOTH,
      label: translations.both,
    },
  ]

  const exportTypes = [
    {
      value: ExportType.ACTIVITIES,
      label: translations.hours,
    },
    {
      value: ExportType.DIET,
      label: translations.diet,
    },
    {
      value: ExportType.PAUSE,
      label: translations.pauseExport,
    },
    {
      value: ExportType.VEHICLE,
      label: translations.vehicle,
    },
  ]

  const exportDate = [
    {
      value: ExportDate.CUSTOM,
      label: translations.period,
    },
    {
      value: ExportDate.PER_DAY,
      label: translations.perDay,
    },
    {
      value: ExportDate.PER_WEEK,
      label: translations.week,
    },
    {
      value: ExportDate.PER_MONTH,
      label: translations.perMonth,
    },
    {
      value: ExportDate.PER_YEAR,
      label: translations.perYear,
    },
  ]
  const salaryFormats = [
    ExportFormat.VISMA,
    ExportFormat.POWEROFFICE,
    ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
    ExportFormat.TRIPLETEX,
    ExportFormat.PDF_SALARY,
  ]

  const exportOptions = useExportOptions(true)
  const excludeFormats =
    form.exportOption === ExportOption.DOWNLOAD
      ? [
          ExportFormat.EXCEL,
          ExportFormat.EXCEL_FLAT,
          ExportFormat.VISMA,
          ExportFormat.POWEROFFICE,
          ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
          ExportFormat.TRIPLETEX,
        ]
      : form.exportType === ExportType.DIET
      ? salaryFormats
      : []
  const exportFormatOptions = useExportFormatOptions(excludeFormats)

  useEffect(() => {
    if (
      form.exportOption === ExportOption.DOWNLOAD &&
      !(
        form.exportFormat === ExportFormat.PDF ||
        form.exportFormat === ExportFormat.PDF_SALARY
      )
    ) {
      updateForm({
        exportFormat: ExportFormat.PDF,
      })
    }
  }, [form.exportFormat, form.exportOption, updateForm])

  useEffect(() => {
    if (form.exportType !== ExportType.ACTIVITIES && form.weekly) {
      if (form.exportType === ExportType.VEHICLE) return
      updateForm({
        weekly: false,
      })
    }
    if (
      form.exportType !== ExportType.ACTIVITIES &&
      form.exportFormat === ExportFormat.VISMA
    ) {
      updateForm({
        exportFormat: ExportFormat.PDF,
      })
    }
  }, [form.exportFormat, form.exportType, form.weekly, updateForm])

  const handleClick = (value: ExportDate | null) => {
    const currentDate = new Date()

    switch (value) {
      case ExportDate.PER_MONTH: {
        const date = startOfMonth(currentDate)
        const dateEnd = endOfMonth(currentDate)
        updateForm({
          date,
          dateEnd,
          dateType: (value ?? ExportDate.CUSTOM) as ExportDate,
        })
        break
      }
      case ExportDate.PER_YEAR: {
        const date = startOfYear(currentDate)
        const dateEnd = endOfYear(currentDate)
        updateForm({
          date,
          dateEnd,
          dateType: (value ?? ExportDate.CUSTOM) as ExportDate,
        })
        break
      }
      case ExportDate.PER_DAY: {
        updateForm({
          date: startOfDay(currentDate),
          dateEnd: endOfDay(currentDate),
          dateType: (value ?? ExportDate.CUSTOM) as ExportDate,
        })
        break
      }
      case ExportDate.CUSTOM:
        updateForm({
          date: startOfWeek(currentDate, { weekStartsOn: 1 }),
          dateEnd: endOfWeek(currentDate, { weekStartsOn: 1 }),
          dateType: (value ?? ExportDate.CUSTOM) as ExportDate,
        })
        break
      case ExportDate.PER_WEEK:
        updateForm({
          date: startOfWeek(currentDate, { weekStartsOn: 1 }),
          dateEnd: endOfWeek(currentDate, { weekStartsOn: 1 }),
          dateType: (value ?? ExportDate.CUSTOM) as ExportDate,
        })
        break
      default:
        break
    }
  }

  return (
    <ModalStyled
      overflowY="auto"
      title={translations.exportDrivingData}
      width="400px"
      submitDisabled={!formValid}
      submitText={translations.export}
      onCancel={() => onSubmit(null)}
      onSubmit={submit(onSubmit)}
    >
      <Form>
        <Field>
          <label>{translations.type}</label>
          <Select
            value={form.exportType}
            options={exportTypes}
            fullWidth
            height="3.875rem"
            onChange={value =>
              updateForm({ exportType: value ?? ExportType.ACTIVITIES })
            }
          />
        </Field>
        {form.exportType === ExportType.VEHICLE && (
          <>
            {[ExportMode.BOTH, ExportMode.VEHICLE].includes(form.modeType) && (
              <Field>
                <label>{translations.vehicle}</label>
                <VehicleSelect
                  mode="single"
                  value={form.vehicle ?? ''}
                  clearable
                  className="w-full"
                  onChange={vehicle =>
                    updateForm({ vehicle: vehicle?.id ?? null })
                  }
                />
                {!!errors.vehicle && <FieldError>{errors.vehicle}</FieldError>}
              </Field>
            )}
            <Field>
              <label>{translations.modeType}</label>
              <Select
                value={form.modeType}
                options={exportMods}
                fullWidth
                height="3.875rem"
                onChange={value =>
                  updateForm({ modeType: value ?? ExportMode.BOTH })
                }
              />
            </Field>
          </>
        )}
        {form.exportType !== ExportType.VEHICLE && (
          <Field>
            <label>{translations.exportWeekly}</label>
            <MonthWeekToggle
              value={form.weekly}
              onChange={value =>
                updateForm({
                  weekly: value,
                })
              }
            />
          </Field>
        )}

        {form.exportType === ExportType.VEHICLE && (
          <>
            <Field>
              <label>{translations.period}</label>
              <Select
                value={form.dateType ?? ExportDate.CUSTOM}
                options={exportDate}
                fullWidth
                height="3.875rem"
                onChange={handleClick}
              />
            </Field>
            {form.dateType === ExportDate.PER_WEEK && (
              <Field>
                <label>{translations.week}</label>
                <WeekPicker
                  value={form.date ?? new Date()}
                  onChange={selectedDate => {
                    if (!selectedDate) return
                    const weekStart = startOfWeek(selectedDate, {
                      weekStartsOn: 1,
                    })
                    const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 })

                    updateForm({
                      date: startOfDay(weekStart),
                      dateEnd: endOfDay(weekEnd),
                    })
                  }}
                />
              </Field>
            )}
            {form.dateType === ExportDate.PER_DAY && (
              <Field>
                <label>{translations.perDay}</label>
                <DatePicker
                  width="auto"
                  value={form.date ?? new Date()}
                  onChange={e =>
                    updateForm({ date: startOfDay(e), dateEnd: endOfDay(e) })
                  }
                />
              </Field>
            )}
            {form.dateType === ExportDate.PER_MONTH && (
              <Field>
                <label>{translations.perMonth}</label>
                <MonthPicker
                  value={form.date ?? new Date()}
                  onChange={selectedDate => {
                    if (!selectedDate) return

                    const year = selectedDate.getFullYear()
                    const month = selectedDate.getMonth()

                    const firstDayOfMonth = new Date(year, month, 1)

                    const lastDayOfMonth = new Date(year, month + 1, 0)

                    updateForm({
                      date: startOfDay(firstDayOfMonth),
                      dateEnd: endOfDay(lastDayOfMonth),
                    })
                  }}
                  futureYears={0}
                  minWidth="0"
                  width="100%"
                  maxWidth="100%"
                />
              </Field>
            )}

            {form.dateType === ExportDate.PER_YEAR && (
              <Field>
                <label>{translations.perYear}</label>
                <YearSelect
                  value={form.date ?? new Date()}
                  onChange={selectedDate => {
                    if (!selectedDate) return

                    const year = selectedDate.getFullYear()

                    const firstDayOfYear = new Date(year, 0, 1)
                    const lastDayOfYear = new Date(year, 11, 31)

                    updateForm({
                      date: startOfDay(firstDayOfYear),
                      dateEnd: endOfDay(lastDayOfYear),
                    })
                  }}
                />
              </Field>
            )}
            {form.dateType === ExportDate.CUSTOM && (
              <Field>
                <label>{translations.period}</label>
                <DateRangePickerCustom
                  width="auto"
                  value={{
                    from: form.date ?? new Date(),
                    to: form.dateEnd ?? new Date(),
                  }}
                  onChange={e =>
                    updateForm({
                      date: startOfDay(e.from),
                      dateEnd: endOfDay(e.to),
                    })
                  }
                />
              </Field>
            )}
          </>
        )}
        <Field>
          <label>{translations.exportOption}</label>
          <Select
            value={form.exportOption}
            options={exportOptions}
            fullWidth
            height="3.875rem"
            onChange={value =>
              updateForm({
                exportOption: value ?? ExportOption.DOWNLOAD,
              })
            }
          />
        </Field>
        <Field>
          <label>{translations.exportFormat}</label>
          <Select
            value={form.exportFormat}
            options={exportFormatOptions}
            fullWidth
            height="3.875rem"
            onChange={value =>
              updateForm({
                exportFormat: value ?? ExportFormat.PDF,
              })
            }
          />
        </Field>
        {salaryFormats.includes(form.exportFormat) && (
          <Field>
            <label>{translations.detailed}</label>
            <Checkbox
              value={form.detailed ?? false}
              onChange={value =>
                updateForm({
                  detailed: value === 'indeterminate' ? false : value,
                })
              }
            />
          </Field>
        )}
      </Form>
    </ModalStyled>
  )
}
